/**
 * Dashboard widget spec — the shared contract between the AI chat (```widget fenced blocks),
 * the dashboard widget grid, and papaya's /widgets endpoints.
 *
 * Persisted shape: dashboardconfig.widgets = { [widgetId]: DashboardWidget }
 * See .context/widget-feature-design.md for the full design.
 */

export type WidgetKind = 'metric' | 'chart' | 'table' | 'map'

/** Geographic level a map widget colours (mirrors papaya's spec). */
export type WidgetMapScope = 'world' | 'country' | 'counties'
export type WidgetChartType = 'column' | 'pie' | 'line'
export type WidgetSize = 'sm' | 'md' | 'lg'

/** Row-label renderings (mirrors papaya's spec — see formatRowLabel). */
export type WidgetLabelFormat = 'date' | 'day' | 'month' | 'week_index' | 'week_of_year'

const LABEL_FORMATS: WidgetLabelFormat[] = ['date', 'day', 'month', 'week_index', 'week_of_year']

export interface WidgetExtract {
  path?: string
  labelField?: string
  valueField?: string
  seriesFields?: string[]
  /** Keep only rows whose label equals this value (case-insensitive), pre-aggregate */
  labelEquals?: string
  /**
   * Read a STRING field as the widget's answer (e.g. the eBay store name out of
   * list_connected_platforms) — the numeric path can't express a text answer.
   */
  textField?: string
  /**
   * How to render each row's label — trends buckets arrive as the bucket's start
   * date, so a weekly chart reads "2026-07-12" without this.
   */
  labelFormat?: WidgetLabelFormat
  /**
   * How to reduce the extracted rows to one value. The numeric aggregates yield a
   * number; `argmax`/`argmin` yield the LABEL of the winning row (plus its number),
   * which is what makes "the top channel" expressible.
   */
  aggregate?: 'sum' | 'avg' | 'last' | 'count' | 'max' | 'min' | 'argmax' | 'argmin'
}

export interface WidgetMcpSource {
  type: 'mcp_tool'
  tool: string
  params: Record<string, any>
  extract?: WidgetExtract
}

export interface WidgetBuiltinStatSource {
  type: 'builtin_stat'
  stat: string
}

/** Numbers derived from the dashboard's own state (range length, etc.). */
export interface WidgetContextSource {
  type: 'context'
  field: 'days_in_range'
}

export interface ExpressionInput {
  key: string
  label?: string
  source: WidgetBuiltinStatSource | WidgetContextSource | WidgetMcpSource
}

export interface WidgetExpressionSource {
  type: 'expression'
  inputs: ExpressionInput[]
  formula: string
}

export interface WidgetReportSource {
  type: 'report'
  reportId: string
  searchType?: string
}

export type WidgetSource = WidgetMcpSource | WidgetExpressionSource | WidgetReportSource

export interface WidgetFormat {
  isCurrency?: boolean
  prefix?: string
  suffix?: string
  decimals?: number
}

export type WidgetDisplayTone = 'neutral' | 'positive' | 'negative' | 'warning'

/**
 * One threshold rule. Comparators are ANDed, so a single rule can express a band
 * (`{gte: 1000, lt: 5000}`). Rules are evaluated in order; the first match wins.
 */
export interface WidgetDisplayRule {
  gte?: number
  gt?: number
  lte?: number
  lt?: number
  eq?: number
  text: string
  tone?: WidgetDisplayTone
}

/** String match rule — compares the metric's TEXT subject (e.g. an argmax label). */
export interface WidgetMatchRule {
  /** Case-insensitive exact match */
  equals?: string
  /** Case-insensitive match against any member */
  in?: string[]
  /** Case-insensitive substring */
  contains?: string
  text: string
  tone?: WidgetDisplayTone
}

/**
 * Declarative value → label mapping for metric widgets. Lets a metric read
 * "BINGO" while still computing a real value underneath. Pure data — no
 * expressions, no code execution.
 *
 * - `threshold` compares the metric's NUMBER (revenue over 10000)
 * - `match` compares the metric's TEXT (top channel is ebay)
 */
export type WidgetDisplay =
  | {
      type: 'threshold'
      rules: WidgetDisplayRule[]
      fallback?: { text: string; tone?: WidgetDisplayTone }
      /** Show the underlying formatted value beneath the label (default true) */
      showValue?: boolean
    }
  | {
      type: 'match'
      rules: WidgetMatchRule[]
      fallback?: { text: string; tone?: WidgetDisplayTone }
      showValue?: boolean
    }

export interface WidgetMapRegion {
  name: string
  /** ISO-2 country code, or a province/state code when the server had one */
  code?: string
  /** Owning state — the disambiguator a county needs */
  state?: string
  value: number | null
}

export type WidgetData =
  /** `text` carries a label subject (argmax/argmin winner) alongside its number */
  | { kind: 'metric'; value: number | null; text?: string | null }
  | { kind: 'chart'; categories: string[]; series: { name: string; data: (number | null)[] }[] }
  | { kind: 'table'; columns: string[]; rows: Record<string, any>[] }
  /** One value per region; the client resolves each to map geometry */
  | {
      kind: 'map'
      scope: WidgetMapScope
      country?: string
      measure?: string
      regions: WidgetMapRegion[]
    }

/** Where the widget renders on the dashboard. */
export type WidgetPlacement = 'widgets' | 'metrics'

export interface DashboardWidget {
  id: string
  title: string
  kind: WidgetKind
  chart_type?: WidgetChartType
  size?: WidgetSize
  order: number
  visible: boolean
  source: WidgetSource
  format?: WidgetFormat
  /** metric widgets only — maps the computed number to a label */
  display?: WidgetDisplay
  /**
   * 'metrics' promotes the widget into the top KPI row alongside the built-in
   * metrics; 'widgets' (default) puts it in the Custom Widgets grid below.
   * Only metric-kind widgets can sit in the KPI row.
   */
  placement?: WidgetPlacement
  /**
   * Position inside the top KPI row, in the SAME index space as the built-in
   * metrics' config order — that is what lets a custom tile be dragged in between
   * built-in ones. Absent means "not placed yet": the tile sorts after every
   * built-in metric (which is where promoted widgets have always landed).
   */
  metricOrder?: number
  /**
   * Position inside the facet chart grid, in the same index space as the built-in
   * charts' config order. Absent means "not placed yet": the tile sorts after
   * every built-in chart.
   */
  gridOrder?: number
  /**
   * Custom tab this widget belongs to (dashboardconfig.tabs key). Absent = the main
   * Overview page, which is where every widget lived before custom tabs existed.
   */
  tab?: string
  /** map widgets only — geographic level (auto-detected from the facet when absent) */
  map_scope?: WidgetMapScope
  /** false = ignore the dashboard filter sidebar (default true) */
  respectDashboardFilters?: boolean
  prompt?: string
  createdBy?: 'ai' | 'user'
  /** Transient preview data (AI chat) — strip before persisting */
  preview?: WidgetData
  /** Transient: why the preview is missing (blocked integration, no account, …) */
  previewError?: string
  /** Transient: execution identity of preview data, so saving does not refetch it. */
  previewExecutionKey?: string
}

/**
 * A user-defined dashboard tab. Persisted as dashboardconfig.tabs = { [id]: tab },
 * holding only widgets whose `tab` points at it — the built-in views (Overview,
 * Orders, …) are code, not config, and are unaffected.
 *
 * Deleting is a soft hide (like widgets and built-in charts) because the config save
 * merges keys: an absent key means "unchanged", so it could not express a removal.
 */
export interface DashboardTab {
  id: string
  title: string
  order: number
  visible: boolean
}

/** Longest tab title the tab strip renders without truncating awkwardly. */
export const MAX_TAB_TITLE_LENGTH = 30

export const makeTabId = () => `t_${Math.random().toString(36).slice(2, 10)}`

const WIDGET_KINDS: WidgetKind[] = ['metric', 'chart', 'table', 'map']
const MAP_SCOPES: WidgetMapScope[] = ['world', 'country', 'counties']
const CHART_TYPES: WidgetChartType[] = ['column', 'pie', 'line']
const SIZES: WidgetSize[] = ['sm', 'md', 'lg']

/** Max expression inputs per widget — mirrors papaya's spec (bounds MCP fan-out). */
export const MAX_EXPRESSION_INPUTS = 8

/**
 * Known-good read-only MCP tools. MUST stay in sync with papaya's
 * WIDGET_READONLY_TOOLS — widgets auto-execute repeatedly with no confirmation,
 * so a mutating tool must never be reachable. Rejecting here means an AI-emitted
 * widget with a mutating tool renders "Widget unavailable" instead of offering an
 * "Add to Dashboard" button. Tools outside this list are judged by name (see
 * isReadOnlyWidgetTool) so integration reads aren't blocked by a stale list.
 */
export const WIDGET_READONLY_TOOLS: ReadonlySet<string> = new Set([
  'get_cross_platform_ads_performance',
  'search_google_ads',
  'search_facebook_ads',
  'search_amazon_ads',
  'search_tiktok',
  'search_pinterest',
  'search_klaviyo',
  'search_stripe',
  'get_website_traffic_data',
  'get_website_traffic_metrics',
  'get_website_traffic_summary',
  'get_orders_report',
  'get_line_items_report',
  'get_charts_report',
  'get_trends_report',
  'get_year_comparison_report',
  'get_customers_report',
  'get_products_report',
  'get_product_trends_report',
  'get_variants_report',
  'get_brands_report',
  'get_skus_report',
  'get_custom_order_bases_report',
  'get_marketing_report',
  'get_marketing_mix_model_summary',
  'get_marketing_mix_channel_contributions',
  'get_marketing_budget_scenarios',
  'run_google_analytics_report',
  'get_inventory_summary',
  'get_inventory_products',
  'get_inventory_variants',
  'get_inventory_brands',
  'gmc_get_performance_report',
])

/**
 * Tools whose params accept the dashboard's filter sidebar (a Solr `filter_config`).
 * MUST stay in sync with papaya's REPORT_TOOLS_ACCEPTING_FILTER_CONFIG — the executor
 * is what actually merges the filter; this copy exists so a card can TELL the user
 * whether its number moves when they change a filter.
 *
 * Everything else (ad platforms, Stripe, Klaviyo, analytics, traffic, inventory) has
 * no view of the merchant's orders, so an order filter cannot be expressed against
 * it at all — those widgets follow the date range only.
 */
export const FILTER_AWARE_WIDGET_TOOLS: ReadonlySet<string> = new Set([
  'get_orders_report',
  'get_line_items_report',
  'get_charts_report',
  'get_trends_report',
  'get_year_comparison_report',
  'get_customers_report',
  'get_products_report',
  'get_product_trends_report',
  'get_variants_report',
  'get_brands_report',
  'get_skus_report',
  'get_custom_order_bases_report',
  'get_marketing_report',
])

/**
 * How the dashboard's filter sidebar affects a widget:
 * - `filtered`   every input follows the filters
 * - `partial`    a calculation mixing filtered and unfiltered inputs (sales ÷ ad spend)
 * - `date_only`  follows the date range, but no filter can reach its source
 * - `ignored`    the widget opted out (respectDashboardFilters: false)
 */
export type WidgetFilterScope = 'filtered' | 'partial' | 'date_only' | 'ignored'

const sourceFollowsFilters = (source: WidgetSource | WidgetBuiltinStatSource | WidgetContextSource): boolean => {
  if (source.type === 'mcp_tool') return FILTER_AWARE_WIDGET_TOOLS.has(source.tool)
  // A builtin stat IS the dashboard's own filtered number
  if (source.type === 'builtin_stat') return true
  // Range length: unaffected by filters either way, so it never decides the verdict
  if (source.type === 'context') return true
  // Saved reports carry their OWN filters; the dashboard's are not applied
  return false
}

export const widgetFilterScope = (widget: Pick<DashboardWidget, 'source' | 'respectDashboardFilters'>): WidgetFilterScope => {
  if (widget.respectDashboardFilters === false) return 'ignored'
  const source = widget.source
  if (source.type === 'expression') {
    // `context` inputs are filter-neutral — counting them would call a widget made
    // of one ad-spend input plus days_in_range "partly filtered", which it isn't.
    const decisive = (source.inputs || []).filter(input => input.source.type !== 'context')
    if (!decisive.length) return 'date_only'
    const followed = decisive.filter(input => sourceFollowsFilters(input.source)).length
    if (followed === decisive.length) return 'filtered'
    return followed ? 'partial' : 'date_only'
  }
  return sourceFollowsFilters(source) ? 'filtered' : 'date_only'
}

/** Built-in dashboard stats referenced by an expression widget. */
export const widgetBuiltinStatKeys = (widget: DashboardWidget): string[] =>
  widget.source.type === 'expression'
    ? widget.source.inputs
        .filter(input => input.source.type === 'builtin_stat')
        .map(input => (input.source as WidgetBuiltinStatSource).stat)
    : []

/**
 * Stable identity for the inputs that determine a widget result. Generation and
 * rendering share this so a fresh preview is not immediately fetched and billed again.
 */
export const widgetExecutionKey = (
  widget: DashboardWidget,
  context: {
    rangeKey: string
    builtinStats: Record<string, number>
    filterConfig: string
  }
): string => {
  const statKeys = widgetBuiltinStatKeys(widget)
  const builtinPart = statKeys.length
    ? '|b:' + statKeys.map(key => `${key}=${context.builtinStats[key] ?? ''}`).join(',')
    : ''
  const scope = widgetFilterScope(widget)
  const filterPart =
    scope === 'filtered' || scope === 'partial' ? `|f:${context.filterConfig}` : ''
  return `${context.rangeKey}|${JSON.stringify([
    widget.kind,
    widget.chart_type,
    widget.source,
  ])}${builtinPart}${filterPart}`
}

/** Short card badge for a scope — null when the widget simply follows the filters. */
export const widgetFilterBadge = (scope: WidgetFilterScope): { label: string; title: string } | null => {
  if (scope === 'filtered') return null
  if (scope === 'partial') {
    return {
      label: 'Partly filtered',
      title:
        'Part of this calculation follows your dashboard filters and part cannot — outside platforms (ad spend, Stripe, analytics) have no view of your orders.',
    }
  }
  if (scope === 'ignored') {
    return {
      label: 'Unfiltered',
      title: 'This widget is set to ignore the dashboard filters. It always reports the whole account for the selected dates.',
    }
  }
  return {
    label: 'Unfiltered',
    title:
      'Your dashboard filters do not apply to this widget — its data comes from outside PayHelm orders, so only the date range affects it.',
  }
}

/**
 * Two-bucket facets come back under DISPLAY labels, not the underlying values:
 * is_first_order is "New"/"Returning", not true/false. A spec asking for the raw value
 * matched no bucket and the card rendered a dash. Mirror of papaya's
 * FACET_LABEL_SYNONYMS — keep the two in sync; papaya's copy is the one that runs for
 * MCP widgets, this one serves report-sourced widgets fetched client-side.
 */
const FACET_LABEL_SYNONYMS: Record<string, string[]> = {
  new: ['true', 'first', 'first order', 'first-time', 'first time', 'new customer', 'first time buyer'],
  returning: ['false', 'repeat', 'repeat buyer', 'repeat buyers', 'repeat customer', 'returning customer', 'existing', 'existing customer'],
  taxed: ['true'],
  'non-taxed': ['false', 'non taxed', 'untaxed', 'no tax'],
  discounted: ['true'],
  'non-discounted': ['false', 'non discounted', 'undiscounted', 'no discount'],
  guest: ['true', 'guest checkout'],
  'non-guest': ['false', 'non guest', 'registered', 'account holder'],
  'with staff note': ['true', 'noted', 'has note', 'with note'],
  'without staff note': ['false', 'unnoted', 'no note', 'without note'],
  'yes gift cards': ['true', 'gift card', 'gift cards', 'with gift card'],
  'no gift cards': ['false', 'no gift card', 'without gift card'],
}

/**
 * The canonical bucket label a requested value resolves to ("false" → "Returning"), for
 * display. Returns the input unchanged when it isn't a known equivalence.
 */
export const canonicalFacetLabel = (requested: string): string => {
  const wanted = String(requested ?? '').trim().toLowerCase()
  if (!wanted) return requested
  const canonical = Object.keys(FACET_LABEL_SYNONYMS).find(label =>
    FACET_LABEL_SYNONYMS[label].includes(wanted)
  )
  if (!canonical) return requested
  // Title Case the canonical key back into its display form ("non-taxed" → "Non-Taxed")
  return canonical.replace(/\b\w/g, c => c.toUpperCase())
}

/** Does this bucket label satisfy the requested one (exact, or a known equivalence)? */
export const facetLabelMatches = (label: any, requested: string): boolean => {
  const actual = String(label ?? '').trim().toLowerCase()
  const wanted = String(requested ?? '').trim().toLowerCase()
  if (!wanted) return false
  if (actual === wanted) return true
  return (FACET_LABEL_SYNONYMS[actual] || []).includes(wanted)
}

/** Verbs that only ever read (checked on the first two name segments). */
const READ_VERBS = new Set([
  'get', 'list', 'search', 'query', 'lookup', 'fetch', 'check', 'count', 'describe', 'read', 'inspect',
])

/** Verbs that write, spend, or send — one anywhere in the name disqualifies the tool. */
const WRITE_VERBS = new Set([
  'create', 'update', 'delete', 'remove', 'add', 'edit', 'set', 'put', 'patch', 'insert', 'upsert',
  'write', 'send', 'publish', 'reply', 'hide', 'import', 'export', 'upload', 'download',
  'start', 'stop', 'cancel', 'purchase', 'buy', 'pay', 'refund', 'void', 'connect', 'disconnect',
  'toggle', 'enable', 'disable', 'install', 'uninstall', 'revoke', 'rotate', 'manage', 'duplicate',
  'reset', 'restore', 'discard', 'adjust', 'fulfill', 'assign', 'approve', 'archive', 'rename',
  'move', 'merge', 'apply', 'train', 'schedule', 'sync', 'generate', 'bulk', 'preview', 'autostyle',
])

/** Read-only tools whose names don't start with a read verb. */
const EXTRA_READONLY_TOOLS: ReadonlySet<string> = new Set([
  'run_google_analytics_report',
  'run_marketing_budget_simulation',
  'keyword_planner_google_ads',
  'google_web_search',
])

/**
 * Strip the MCP client-prefix a widget's `tool` may carry (`mcp__<family>__`,
 * e.g. `mcp__payhelm__`, `mcp__custom__`, plus the legacy bare `payhelm__`) so
 * classification below sees the bare name. Generic on the family segment
 * rather than a hardcoded list, so a future MCP server (`mcp__shopify__...`)
 * doesn't silently fall through unstripped — matches the pattern in
 * `lychee/src/utils/toolAccess.ts`.
 * The general chat agent's "add this to my dashboard" flow emits the
 * fully-qualified id (`mcp__payhelm__get_trends_report`), which without this
 * strip pushes the read verb past segment 1/2 and gets default-denied.
 */
const stripMcpToolPrefix = (name: string): string =>
  name.replace(/^mcp__[a-z0-9]+__/, '').replace(/^payhelm__/, '')

/**
 * Mirrors papaya's isReadOnlyWidgetTool: name-pattern classification with a
 * default of DENY. Keep the two in sync — papaya's copy is the real gate.
 */
export const isReadOnlyWidgetTool = (tool: string): boolean => {
  const name = stripMcpToolPrefix(String(tool || '').trim().toLowerCase())
  if (!name) return false
  if (WIDGET_READONLY_TOOLS.has(name) || EXTRA_READONLY_TOOLS.has(name)) return true
  const segments = name.split(/[_\-.]+/).filter(Boolean)
  if (!segments.length) return false
  if (segments.some(segment => WRITE_VERBS.has(segment))) return false
  return READ_VERBS.has(segments[0]) || (segments.length > 1 && READ_VERBS.has(segments[1]))
}

export const makeWidgetId = () => `w_${Math.random().toString(36).slice(2, 10)}`

export const defaultWidgetSize = (kind: WidgetKind): WidgetSize =>
  kind === 'metric' ? 'sm' : kind === 'map' ? 'lg' : 'md'

const isPlainObject = (v: any): v is Record<string, any> =>
  !!v && typeof v === 'object' && !Array.isArray(v)

const isNonEmptyString = (v: any): v is string => typeof v === 'string' && v.trim().length > 0

/** Parameters that route report tools through another LLM call are never persisted. */
const WIDGET_LLM_ROUTING_PARAMS = new Set(['user_message'])

const validWidgetParams = (value: Record<string, any>): Record<string, any> =>
  Object.fromEntries(
    Object.entries(value).filter(([key]) => !WIDGET_LLM_ROUTING_PARAMS.has(key.toLowerCase()))
  )

const validExtract = (v: any): WidgetExtract | undefined => {
  if (!isPlainObject(v)) return undefined
  const out: WidgetExtract = {}
  if (isNonEmptyString(v.path)) out.path = v.path
  if (isNonEmptyString(v.labelField)) out.labelField = v.labelField
  if (isNonEmptyString(v.valueField)) out.valueField = v.valueField
  if (isNonEmptyString(v.labelEquals)) out.labelEquals = v.labelEquals.trim()
  if (isNonEmptyString(v.textField)) out.textField = v.textField
  if (LABEL_FORMATS.includes(v.labelFormat)) out.labelFormat = v.labelFormat
  if (Array.isArray(v.seriesFields) && v.seriesFields.every(isNonEmptyString)) out.seriesFields = v.seriesFields
  if (['sum', 'avg', 'last', 'count', 'max', 'min', 'argmax', 'argmin'].includes(v.aggregate)) {
    out.aggregate = v.aggregate
  }
  return Object.keys(out).length ? out : undefined
}

const validSource = (v: any): WidgetSource | null => {
  if (!isPlainObject(v)) return null
  if (v.type === 'mcp_tool') {
    if (!isNonEmptyString(v.tool) || !isPlainObject(v.params)) return null
    if (!isReadOnlyWidgetTool(v.tool)) return null
    const source: WidgetMcpSource = {
      type: 'mcp_tool',
      tool: v.tool.trim(),
      params: validWidgetParams(v.params),
    }
    const extract = validExtract(v.extract)
    if (extract) source.extract = extract
    return source
  }
  if (v.type === 'expression') {
    if (!Array.isArray(v.inputs) || !v.inputs.length || !isNonEmptyString(v.formula)) return null
    if (v.inputs.length > MAX_EXPRESSION_INPUTS) return null
    const inputs: ExpressionInput[] = []
    for (const input of v.inputs) {
      if (!isPlainObject(input) || !isNonEmptyString(input.key) || !isPlainObject(input.source)) return null
      if (input.source.type === 'context') {
        if (input.source.field !== 'days_in_range') return null
        inputs.push({
          key: input.key.trim(),
          label: isNonEmptyString(input.label) ? input.label : undefined,
          source: { type: 'context', field: 'days_in_range' },
        })
      } else if (input.source.type === 'builtin_stat') {
        if (!isNonEmptyString(input.source.stat)) return null
        inputs.push({
          key: input.key.trim(),
          label: isNonEmptyString(input.label) ? input.label : undefined,
          source: { type: 'builtin_stat', stat: input.source.stat.trim() },
        })
      } else if (input.source.type === 'mcp_tool') {
        const src = validSource(input.source)
        if (!src || src.type !== 'mcp_tool') return null
        inputs.push({
          key: input.key.trim(),
          label: isNonEmptyString(input.label) ? input.label : undefined,
          source: src,
        })
      } else {
        return null
      }
    }
    return { type: 'expression', inputs, formula: v.formula.trim() }
  }
  if (v.type === 'report') {
    if (!isNonEmptyString(v.reportId)) return null
    const source: WidgetReportSource = { type: 'report', reportId: v.reportId.trim() }
    if (isNonEmptyString(v.searchType)) source.searchType = v.searchType.trim()
    return source
  }
  return null
}

const validWidgetData = (v: any, kind: WidgetKind): WidgetData | undefined => {
  if (!isPlainObject(v)) return undefined
  if (kind === 'metric') {
    const value = v.value
    if (value !== null && typeof value !== 'number') return undefined
    // `text` is the answer for an argmax/argmin or textField metric. Dropping it
    // left a "match" display with nothing to compare, so the card fell back to the
    // raw number ("134" instead of "Texas is #1").
    const data: WidgetData = { kind: 'metric', value }
    if (isNonEmptyString(v.text)) (data as any).text = v.text.trim()
    return data
  }
  if (kind === 'chart') {
    if (!Array.isArray(v.categories) || !Array.isArray(v.series)) return undefined
    const categories = v.categories.map((c: any) => String(c))
    const series = v.series
      .filter((s: any) => isPlainObject(s) && Array.isArray(s.data))
      .map((s: any) => ({
        name: isNonEmptyString(s.name) ? s.name : 'Value',
        data: s.data.map((d: any) => (typeof d === 'number' && isFinite(d) ? d : null)),
      }))
    if (!series.length) return undefined
    return { kind: 'chart', categories, series }
  }
  if (kind === 'map') {
    if (!Array.isArray(v.regions)) return undefined
    const regions: WidgetMapRegion[] = v.regions
      .filter(isPlainObject)
      .map((r: any) => ({
        name: String(r.name ?? ''),
        ...(isNonEmptyString(r.code) ? { code: r.code } : {}),
        ...(isNonEmptyString(r.state) ? { state: r.state } : {}),
        value: typeof r.value === 'number' && isFinite(r.value) ? r.value : null,
      }))
      .filter((r: WidgetMapRegion) => r.name)
    if (!regions.length) return undefined
    const scope: WidgetMapScope = MAP_SCOPES.includes(v.scope) ? v.scope : 'world'
    return {
      kind: 'map',
      scope,
      ...(isNonEmptyString(v.country) ? { country: v.country } : {}),
      ...(isNonEmptyString(v.measure) ? { measure: v.measure } : {}),
      regions,
    }
  }
  if (kind === 'table') {
    if (!Array.isArray(v.rows)) return undefined
    const rows = v.rows.filter(isPlainObject)
    const columns =
      Array.isArray(v.columns) && v.columns.every(isNonEmptyString)
        ? v.columns
        : rows.length
          ? Object.keys(rows[0])
          : []
    return { kind: 'table', columns, rows }
  }
  return undefined
}

const TONES: WidgetDisplayTone[] = ['neutral', 'positive', 'negative', 'warning']
const COMPARATORS = ['gte', 'gt', 'lte', 'lt', 'eq'] as const

/** Max threshold rules per widget — bounds spec size; more than this is a UX problem. */
export const MAX_DISPLAY_RULES = 10

const validTone = (v: any): WidgetDisplayTone | undefined =>
  TONES.includes(v) ? v : undefined

/**
 * Validate a threshold display block. Metric widgets only — a chart or table has
 * many values, so a single label is meaningless.
 */
const validDisplay = (v: any, kind: WidgetKind): WidgetDisplay | undefined => {
  if (!isPlainObject(v) || kind !== 'metric') return undefined
  if (!Array.isArray(v.rules) || !v.rules.length) return undefined
  if (v.rules.length > MAX_DISPLAY_RULES) return undefined

  const fallback = (() => {
    if (!isPlainObject(v.fallback) || !isNonEmptyString(v.fallback.text)) return undefined
    const out: { text: string; tone?: WidgetDisplayTone } = { text: v.fallback.text.trim() }
    const tone = validTone(v.fallback.tone)
    if (tone) out.tone = tone
    return out
  })()

  if (v.type === 'match') {
    const rules: WidgetMatchRule[] = []
    for (const raw of v.rules) {
      if (!isPlainObject(raw) || !isNonEmptyString(raw.text)) return undefined
      const rule: WidgetMatchRule = { text: raw.text.trim() }
      let matchers = 0
      if (isNonEmptyString(raw.equals)) {
        rule.equals = raw.equals.trim()
        matchers++
      }
      if (Array.isArray(raw.in) && raw.in.length && raw.in.every(isNonEmptyString)) {
        rule.in = raw.in.map((x: string) => x.trim())
        matchers++
      }
      if (isNonEmptyString(raw.contains)) {
        rule.contains = raw.contains.trim()
        matchers++
      }
      // A matcher-less rule would swallow every value — reject it.
      if (!matchers) return undefined
      const tone = validTone(raw.tone)
      if (tone) rule.tone = tone
      rules.push(rule)
    }
    const display: WidgetDisplay = { type: 'match', rules }
    if (fallback) display.fallback = fallback
    if (v.showValue === false) display.showValue = false
    return display
  }

  if (v.type !== 'threshold') return undefined

  const rules: WidgetDisplayRule[] = []
  for (const raw of v.rules) {
    if (!isPlainObject(raw) || !isNonEmptyString(raw.text)) return undefined
    const rule: WidgetDisplayRule = { text: raw.text.trim() }
    let comparators = 0
    for (const key of COMPARATORS) {
      const bound = raw[key]
      if (typeof bound === 'number' && isFinite(bound)) {
        rule[key] = bound
        comparators++
      }
    }
    // A rule with no comparator would swallow every value — reject rather than
    // silently turning it into an always-match.
    if (!comparators) return undefined
    const tone = validTone(raw.tone)
    if (tone) rule.tone = tone
    rules.push(rule)
  }

  const display: WidgetDisplay = { type: 'threshold', rules }
  if (fallback) display.fallback = fallback
  if (v.showValue === false) display.showValue = false
  return display
}

/**
 * Resolve a computed metric value to its display label. Returns null when there
 * is no display block, no value, or nothing matched and no fallback was given —
 * callers then render the plain formatted number.
 */
/**
 * Substitute {text} / {value} in a display label so a rule can name the actual
 * result — a fallback of "{text} is #1" reads "CA is #1" rather than "Not TX".
 */
const DISPLAY_TEMPLATE = /\{\s*(text|value)\s*\}/gi

const fillDisplayTemplate = (
  label: string,
  datum: { value?: number | null; text?: string | null },
  format?: WidgetFormat,
): { text: string; templated: boolean } => {
  DISPLAY_TEMPLATE.lastIndex = 0
  const templated = DISPLAY_TEMPLATE.test(label)
  DISPLAY_TEMPLATE.lastIndex = 0
  if (!templated) return { text: label, templated: false }
  return {
    text: label.replace(DISPLAY_TEMPLATE, (_match, token) =>
      String(token).toLowerCase() === 'text'
        ? (datum.text ?? '').toString()
        : formatWidgetValue(datum.value, format)
    ),
    templated: true,
  }
}

export const resolveWidgetDisplay = (
  subject: number | null | undefined | { value?: number | null; text?: string | null },
  display?: WidgetDisplay,
  format?: WidgetFormat,
  /** `templated` = the label already includes the value/subject, so callers can
   * skip the redundant subtitle underneath. */
): { text: string; tone: WidgetDisplayTone; templated?: boolean } | null => {
  if (!display) return null
  const isDatum = subject !== null && subject !== undefined && typeof subject === 'object'
  const value = isDatum ? (subject as any).value : (subject as number | null | undefined)
  const text = isDatum ? (subject as any).text : undefined
  const fill = (label: string, tone?: WidgetDisplayTone) => {
    const filled = fillDisplayTemplate(label, { value, text }, format)
    return { text: filled.text, tone: tone || ('neutral' as WidgetDisplayTone), templated: filled.templated }
  }

  if (display.type === 'match') {
    if (!isNonEmptyString(text)) return null
    const subjectText = String(text).trim().toLowerCase()
    for (const rule of display.rules) {
      const matches =
        (rule.equals !== undefined && subjectText === rule.equals.toLowerCase()) ||
        (rule.in !== undefined && rule.in.some(m => subjectText === m.toLowerCase())) ||
        (rule.contains !== undefined && subjectText.includes(rule.contains.toLowerCase()))
      if (matches) return fill(rule.text, rule.tone)
    }
    if (display.fallback) return fill(display.fallback.text, display.fallback.tone)
    return null
  }

  if (value === null || value === undefined || !isFinite(value)) return null
  for (const rule of display.rules) {
    const matches =
      (rule.gte === undefined || value >= rule.gte) &&
      (rule.gt === undefined || value > rule.gt) &&
      (rule.lte === undefined || value <= rule.lte) &&
      (rule.lt === undefined || value < rule.lt) &&
      (rule.eq === undefined || value === rule.eq)
    if (matches) return fill(rule.text, rule.tone)
  }
  if (display.fallback) return fill(display.fallback.text, display.fallback.tone)
  return null
}

/** Tailwind text colour per tone, shared by the dashboard card and the chat preview. */
export const widgetToneClass = (tone: WidgetDisplayTone): string => {
  if (tone === 'positive') return 'tw-text-green-600'
  if (tone === 'negative') return 'tw-text-red-600'
  if (tone === 'warning') return 'tw-text-amber-600'
  return 'tw-text-gray-900'
}

/**
 * Lenient validator/normalizer for AI-emitted or hand-written widget specs.
 * Fills id/order/visible/size defaults. Returns null for anything not widget-shaped.
 */
export const toWidgetSpec = (value: any): DashboardWidget | null => {
  if (!isPlainObject(value)) return null
  if (!isNonEmptyString(value.title)) return null
  if (!WIDGET_KINDS.includes(value.kind)) return null
  const source = validSource(value.source)
  if (!source) return null
  // Expressions always resolve to a single number
  if (source.type === 'expression' && value.kind !== 'metric') return null

  const widget: DashboardWidget = {
    id: isNonEmptyString(value.id) ? value.id : makeWidgetId(),
    title: value.title.trim(),
    kind: value.kind,
    size: SIZES.includes(value.size) ? value.size : defaultWidgetSize(value.kind),
    order: typeof value.order === 'number' ? value.order : 0,
    visible: value.visible !== false,
    source,
  }
  if (value.kind === 'chart') {
    widget.chart_type = CHART_TYPES.includes(value.chart_type) ? value.chart_type : 'column'
  }
  if (value.kind === 'map' && MAP_SCOPES.includes(value.map_scope)) {
    widget.map_scope = value.map_scope
  }
  if (isPlainObject(value.format)) {
    const format: WidgetFormat = {}
    if (typeof value.format.isCurrency === 'boolean') format.isCurrency = value.format.isCurrency
    if (isNonEmptyString(value.format.prefix)) format.prefix = value.format.prefix
    if (isNonEmptyString(value.format.suffix)) format.suffix = value.format.suffix
    if (typeof value.format.decimals === 'number' && isFinite(value.format.decimals)) {
      // Clamp to Intl.NumberFormat's legal maximumFractionDigits range (0–20)
      format.decimals = Math.min(20, Math.max(0, Math.floor(value.format.decimals)))
    }
    if (Object.keys(format).length) widget.format = format
  }
  const display = validDisplay(value.display, value.kind)
  if (display) widget.display = display
  // A chart or table in a KPI tile row would blow out the layout — metrics only.
  if (value.placement === 'metrics' && value.kind === 'metric') widget.placement = 'metrics'
  if (typeof value.metricOrder === 'number' && isFinite(value.metricOrder)) {
    widget.metricOrder = value.metricOrder
  }
  if (typeof value.gridOrder === 'number' && isFinite(value.gridOrder)) {
    widget.gridOrder = value.gridOrder
  }
  if (isNonEmptyString(value.tab)) widget.tab = value.tab.trim()
  if (value.respectDashboardFilters === false) widget.respectDashboardFilters = false
  if (isNonEmptyString(value.prompt)) widget.prompt = value.prompt
  if (value.createdBy === 'ai' || value.createdBy === 'user') widget.createdBy = value.createdBy
  const preview = validWidgetData(value.preview, value.kind)
  if (preview) widget.preview = preview
  return widget
}

/** Parse the body of a fenced code block as a widget spec (used for ```widget blocks). */
export const widgetSpecFromFencedCode = (code: string): DashboardWidget | null => {
  const trimmed = (code || '').trim()
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return null
  try {
    return toWidgetSpec(JSON.parse(trimmed))
  } catch (e) {
    return null
  }
}

/** Strip transient fields before persisting into dashboardconfig */
export const persistableWidget = (widget: DashboardWidget): DashboardWidget => {
  const { preview, previewError, previewExecutionKey, ...rest } = widget
  return rest
}

/**
 * Stable content identity for a widget, independent of id/order/visible/preview.
 * Two widgets with the same signature are "the same widget" for dedup and for
 * the AI chat card's already-added state (which must survive a re-parse that
 * mints a fresh random id).
 */
export const widgetContentSignature = (widget: Partial<DashboardWidget>): string =>
  JSON.stringify({
    title: (widget.title || '').trim(),
    kind: widget.kind,
    chart_type: widget.chart_type,
    map_scope: widget.map_scope,
    source: widget.source,
    format: widget.format,
    display: widget.display,
    // The same widget on a different tab is a different widget — otherwise adding
    // "Net Sales" to a new tab would silently reuse the Overview one and the new
    // tab would come up empty. (Absent on Overview widgets, so signatures written
    // before custom tabs existed are unchanged.)
    ...(widget.tab ? { tab: widget.tab } : {}),
  })

/**
 * Normalize one persisted tab entry. Returns null for anything not tab-shaped, so a
 * corrupt config drops that tab rather than rendering a nameless strip label.
 */
export const toDashboardTab = (value: any, fallbackId?: string): DashboardTab | null => {
  if (!isPlainObject(value)) return null
  const id = isNonEmptyString(value.id) ? value.id.trim() : isNonEmptyString(fallbackId) ? fallbackId.trim() : ''
  if (!id) return null
  if (!isNonEmptyString(value.title)) return null
  return {
    id,
    title: value.title.trim().slice(0, MAX_TAB_TITLE_LENGTH),
    order: typeof value.order === 'number' && isFinite(value.order) ? value.order : 0,
    visible: value.visible !== false,
  }
}

/** Visible custom tabs in strip order (ties broken by id so the order is stable). */
export const sortedDashboardTabs = (tabsConfig: Record<string, any> | undefined | null): DashboardTab[] =>
  Object.entries(tabsConfig || {})
    .map(([id, tab]) => toDashboardTab(tab, id))
    .filter((tab): tab is DashboardTab => !!tab && tab.visible)
    .sort((a, b) => (a.order !== b.order ? a.order - b.order : a.id.localeCompare(b.id)))

/**
 * Display label for a visible tab. Duplicate titles keep their stored name and get
 * a (1), (2), … suffix in strip order so the sidebar / breadcrumb / tab strip can
 * show both at once. The tab id remains the stable key; this never mutates config.
 */
export const withDisambiguatedTabTitles = (
  tabs: DashboardTab[]
): Array<DashboardTab & { displayTitle: string }> => {
  const counts = new Map<string, number>()
  for (const tab of tabs) {
    counts.set(tab.title, (counts.get(tab.title) || 0) + 1)
  }
  const seen = new Map<string, number>()
  return tabs.map((tab) => {
    if ((counts.get(tab.title) || 0) < 2) {
      return { ...tab, displayTitle: tab.title }
    }
    const n = (seen.get(tab.title) || 0) + 1
    seen.set(tab.title, n)
    return { ...tab, displayTitle: `${tab.title} (${n})` }
  })
}

/**
 * Builtin stats every visible widget needs, across all pages.
 *
 * Solr does not compute a metric whose dashboardconfig says visible:false — the facet
 * builder deletes it (papaya models/Unified/facets.ts) — so a widget built on a metric
 * the merchant had switched off got nothing back, and the card asked them to turn the
 * metric on. A widget should not depend on the metric list.
 */
export const widgetRequiredStats = (widgetsConfig: Record<string, any> | undefined | null): string[] => {
  const stats = new Set<string>()
  for (const widget of Object.values(widgetsConfig || {})) {
    if (!isPlainObject(widget) || widget.visible === false) continue
    const source = widget.source
    if (!isPlainObject(source) || source.type !== 'expression') continue
    for (const input of source.inputs || []) {
      if (input?.source?.type === 'builtin_stat' && isNonEmptyString(input.source.stat)) {
        stats.add(input.source.stat.trim())
      }
    }
  }
  return [...stats]
}

/**
 * A config copy in which those stats are marked visible, so the search computes them.
 * Request-only — the caller must NOT persist it, or turning a metric off in the list
 * would silently turn itself back on.
 */
export const withWidgetStatsVisible = (config: any): any => {
  const stats = widgetRequiredStats(config?.widgets)
  if (!stats.length) return config
  const overview = { ...(config?.metrics?.overview || {}) }
  let changed = false
  for (const stat of stats) {
    if (overview[stat]?.visible === true) continue
    overview[stat] = { ...(overview[stat] || {}), visible: true }
    changed = true
  }
  if (!changed) return config
  return { ...config, metrics: { ...(config?.metrics || {}), overview } }
}

/**
 * Widgets belonging to one page. `tabId` undefined means the main Overview page, and
 * a widget pointing at a tab that no longer exists comes back there rather than
 * disappearing with the tab.
 */
export const widgetsForTab = (
  widgets: DashboardWidget[],
  tabId: string | undefined,
  knownTabIds?: ReadonlySet<string>
): DashboardWidget[] =>
  widgets.filter(widget => {
    const owner = widget.tab || ''
    if (tabId) return owner === tabId
    return !owner || (!!knownTabIds && !knownTabIds.has(owner))
  })

const NUMBER_FORMAT = (decimals: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: decimals })

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const parseLabelDate = (value: any): Date | null => {
  const text = String(value ?? '').trim()
  if (!text) return null
  const ymd = text.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (ymd) return new Date(Date.UTC(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])))
  const parsed = Date.parse(text)
  return isFinite(parsed) ? new Date(parsed) : null
}

/** ISO-8601 week number (weeks start Monday; week 1 holds the first Thursday). */
const isoWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
  const day = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - day)
  const yearStart = Date.UTC(d.getUTCFullYear(), 0, 1)
  return Math.ceil(((d.getTime() - yearStart) / 86400000 + 1) / 7)
}

/**
 * Render one row label. Mirrors papaya's formatRowLabel: `week_index` numbers the
 * buckets as shown, `week_of_year` uses the calendar week, and anything
 * unparseable keeps the raw label rather than inventing one.
 */
export const formatRowLabel = (
  raw: any,
  format: WidgetLabelFormat | undefined,
  index: number
): string => {
  const text = String(raw ?? '')
  if (!format || format === 'date') return text
  if (format === 'week_index') return `Week ${index + 1}`
  const date = parseLabelDate(raw)
  if (!date) return text
  if (format === 'week_of_year') return `Week ${isoWeekNumber(date)}`
  if (format === 'month') return `${MONTH_ABBR[date.getUTCMonth()]} ${date.getUTCFullYear()}`
  if (format === 'day') return `${MONTH_ABBR[date.getUTCMonth()]} ${date.getUTCDate()}`
  return text
}

export const formatWidgetValue = (value: number | null | undefined, format?: WidgetFormat): string => {
  if (value === null || value === undefined || !isFinite(value as number)) return '—'
  const rawDecimals = typeof format?.decimals === 'number' ? format.decimals : Math.abs(value) >= 100 ? 0 : 2
  // Guard against out-of-range maximumFractionDigits (would throw RangeError)
  const decimals = Math.min(20, Math.max(0, Math.floor(isFinite(rawDecimals) ? rawDecimals : 2)))
  const formatted = NUMBER_FORMAT(decimals).format(value)
  const prefix = format?.isCurrency ? '$' : format?.prefix || ''
  const suffix = format?.suffix || ''
  return `${prefix}${formatted}${suffix}`
}

/**
 * Row fields that identify a row's PLUMBING rather than name it. Mirror of papaya's
 * NON_LABEL_FIELDS — taking "the first string field" as the label charted every order
 * as "Unified_order" (get_orders_report rows lead with doc_type) and every product as
 * "USD" (get_products_report leads with currency).
 */
const NON_LABEL_FIELDS = new Set([
  'doc_type', 'mcp_id', 'merchant_id', 'account_id', 'account_customer_id', 'external_id',
  'platform_id', 'currency', 'id',
])

/** Fields that ARE the name, in the order a reader would expect one. */
const PREFERRED_LABEL_FIELDS = [
  'name', 'title', 'label', 'sku', 'product_title', 'variant_title', 'campaign_name',
  'brand', 'brand_name', 'email', 'channel', 'val',
]

const looksLikePlumbing = (key: string): boolean =>
  NON_LABEL_FIELDS.has(key) || /(^|_)(id|ids|link|url|href|image|thumbnail)$/.test(key)

/**
 * Choose the column to label rows by: an actual name first, then any non-plumbing
 * string column that VARIES across rows — one repeated value labels every category
 * identically, which is the tell that it describes the response, not the row.
 */
export const pickLabelField = (
  rows: Record<string, any>[],
  stringKeys: string[]
): string | undefined => {
  if (!stringKeys.length) return undefined
  for (const preferred of PREFERRED_LABEL_FIELDS) {
    if (stringKeys.includes(preferred)) return preferred
  }
  const usable = stringKeys.filter(key => !looksLikePlumbing(key))
  const sampled = rows.slice(0, 20)
  const varying = usable.find(key => new Set(sampled.map(row => row?.[key])).size > 1)
  return varying || usable[0] || stringKeys[0]
}

/**
 * Generic client-side normalizer: rows of objects -> WidgetData for a chart/table widget.
 * Used for report-sourced widgets (fetched client-side) and defensive re-normalization.
 */
export const rowsToWidgetData = (
  allRows: Record<string, any>[],
  widget: Pick<DashboardWidget, 'kind' | 'source'>
): WidgetData | null => {
  if (!Array.isArray(allRows) || !allRows.length) return null
  const extract = (widget.source as WidgetMcpSource).extract || {}
  // Narrow to one labelled row when asked (matches papaya's executor).
  const rows = extract.labelEquals
    ? allRows.filter(r => {
        const label = extract.labelField ? r[extract.labelField] : (r.val ?? r.title)
        return facetLabelMatches(label, extract.labelEquals as string)
      })
    : allRows
  if (!rows.length) {
    // Asked for a row that isn't there — empty beats aggregating the others.
    return widget.kind === 'metric' ? { kind: 'metric', value: null, text: null } : null
  }
  const sample = rows[0]
  const keys = Object.keys(sample)
  const numericKeys = keys.filter(k => typeof sample[k] === 'number' && isFinite(sample[k]))
  const stringKeys = keys.filter(k => typeof sample[k] === 'string')
  const labelField = extract.labelField || pickLabelField(rows, stringKeys) || keys[0]
  const valueField = extract.valueField || numericKeys[0]

  if (widget.kind === 'table') {
    return { kind: 'table', columns: keys.slice(0, 8), rows: rows.slice(0, 10) }
  }
  if (widget.kind === 'chart') {
    if (!valueField) return null
    const seriesFields = extract.seriesFields?.length ? extract.seriesFields : [valueField]
    return {
      kind: 'chart',
      categories: rows.map((r, index) => formatRowLabel(r[labelField], extract.labelFormat, index)),
      series: seriesFields.map(field => ({
        name: field,
        data: rows.map(r => (typeof r[field] === 'number' && isFinite(r[field]) ? r[field] : null)),
      })),
    }
  }
  if (widget.kind === 'metric') {
    // A text answer (a store name, an account id): first row that has one.
    if (extract.textField) {
      const textField = extract.textField
      const row = rows.find(r => typeof r[textField] === 'string' && String(r[textField]).trim() !== '')
      if (!row) return { kind: 'metric', value: null, text: null }
      const number = valueField && typeof row[valueField] === 'number' && isFinite(row[valueField])
        ? row[valueField]
        : null
      return { kind: 'metric', value: number, text: String(row[textField]).trim() }
    }
    if (!valueField) return null
    // argmax/argmin: the answer is WHICH row won — return its label plus its number.
    if (extract.aggregate === 'argmax' || extract.aggregate === 'argmin') {
      const wantMax = extract.aggregate === 'argmax'
      let best: { label: string; value: number } | null = null
      for (const row of rows) {
        const num = row[valueField]
        if (typeof num !== 'number' || !isFinite(num)) continue
        if (!best || (wantMax ? num > best.value : num < best.value)) {
          best = { label: String(row[labelField] ?? ''), value: num }
        }
      }
      return best ? { kind: 'metric', value: best.value, text: best.label } : { kind: 'metric', value: null, text: null }
    }
    const values = rows.map(r => r[valueField]).filter((v: any) => typeof v === 'number' && isFinite(v))
    if (!values.length) return { kind: 'metric', value: null }
    const aggregate = extract.aggregate || 'sum'
    let value: number
    if (aggregate === 'avg') value = values.reduce((a: number, b: number) => a + b, 0) / values.length
    else if (aggregate === 'last') value = values[values.length - 1]
    else if (aggregate === 'count') value = values.length
    else if (aggregate === 'max' || aggregate === 'argmax') value = Math.max(...values)
    else if (aggregate === 'min' || aggregate === 'argmin') value = Math.min(...values)
    else value = values.reduce((a: number, b: number) => a + b, 0)
    return { kind: 'metric', value }
  }
  return null
}
